// Shared manager for select2 widget initialization

import { attachMobileSelectSummary } from "./mobile_select_summary"
import { isTouchPrimary } from "./mobile_viewport"

const DEFAULT_RETRY_DELAY = 50
const DEFAULT_RETRY_LIMIT = 40

// Re-exported for back-compat: isTouchPrimary now lives in mobile_viewport
// alongside isMobileSheetViewport, so the two "is this mobile?" predicates sit
// in one place and can be read against each other.
export { isTouchPrimary }

const select2Available = () => {
  const jQuery = window.jQuery || window.$
  return jQuery && jQuery.fn && typeof jQuery.fn.select2 === "function" ? jQuery : null
}

// Give the rendered Select2 control the source select's accessible name.
//
// Select2 keeps the original <select> in the DOM but marks it aria-hidden and
// renders its own role="combobox" span as the real control. Confirmed against
// Chrome's computed accessibility tree: the source node comes back
// `ignored: true, ignoredReasons: ["ariaHiddenElement"]` (so an aria-label on it
// reaches nothing), while `.select2-selection` is exposed with an EMPTY name.
// A field labelled only by aria-label — because a heading titles it rather than
// a <label>, as with Licensed States — is therefore announced unnamed. Copy the
// name onto the node that is actually in the tree.
//
// No-op when the source has no aria-label, so fields labelled the usual way
// (a real <label>, which Select2 does associate) are untouched.
//
// A multi-select is the exception (#13405): its Tab stop and combobox is the
// inline search field, and the span around it becomes a plain box (see
// transferComboboxRole), so the name goes onto the field — replacing Select2's
// generic "Search" — and never onto the span, where it would name nothing.
function mirrorAccessibleName(element, $element) {
  const label = element.getAttribute("aria-label")
  if (!label) return

  const rendered = renderedSelection(element, $element)
  const target = multipleSearchField(rendered) || rendered
  if (target) target.setAttribute("aria-label", label)
}

// Select2 renders its container immediately after the source select; prefer
// the instance's own reference and fall back to that DOM position.
function renderedSelection(element, $element) {
  const container = $element?.data?.("select2")?.$container?.[0] || element.nextElementSibling
  return container?.querySelector?.(".select2-selection") || null
}

// The inline search field of a MULTIPLE selection, or null for a single one.
function multipleSearchField(selection) {
  if (!selection?.classList?.contains("select2-selection--multiple")) return null
  return selection.querySelector(".select2-search__field")
}

// ── Widget accessibility repairs (#13405) ───────────────────────────────────
//
// Select2 4.1.0-rc.0 renders three things the design contract forbids, on every
// page that mounts a multi-select:
//
//   1. `button.select2-selection__clear` ("Remove all items") and every
//      `button.select2-selection__choice__remove` ("Remove item") carry a native
//      `title` — BUTTON-4 bans native titles on controls in favour of the
//      CSS-only `data-gl-tooltip` bubble plus an `aria-label`.
//   2. Those buttons and the `textarea.select2-search__field` nest inside the
//      `span.select2-selection[role="combobox"]` — A11Y-2, interactive controls
//      never nest. For a multi-select the span is not the control at all:
//      Select2's Search decorator moves the source's tabindex onto the search
//      field (the span is left at -1), wires `aria-controls` /
//      `aria-activedescendant` / `aria-autocomplete` onto the field, and opens,
//      moves and selects from its keydown. The ARIA 1.2 combobox pattern names
//      that field the combobox, so the span hands over its role and its popup
//      state; keyboard behaviour is untouched because none of it keyed on the
//      role. Single selects keep the span as their control (it holds the
//      tabindex there) and are left alone.
//   3. The search field paints no focus ring — that half is CSS
//      (components/_select2_a11y.scss).
//
// The buttons are re-rendered from scratch by MultipleSelection#update (and
// AllowClear#update prepends a fresh clear button) on every `selection:update`
// — init, a user selection, a programmatic `.trigger("change")`, an <option>
// subtree mutation — so the title repair runs once at init and again after
// every re-render, on the instance's own event, registered after the library's
// own listener so it always sees the finished DOM. Decorating the adapter
// prototypes instead would miss the clear button: Utils.Decorate copies the
// base `update` at decoration time and AllowClear appends its button after it.

const TOOLTIP_ATTR = "data-gl-tooltip"
const TITLED_BUTTONS = ".select2-selection__clear[title], .select2-selection__choice__remove[title]"
const CHOICE_SELECTOR = ".select2-selection__choice[title]"
const CHOICE_DISPLAY_SELECTOR = ".select2-selection__choice__display"

// Move each button's native `title` into `data-gl-tooltip` + `aria-label`
// (the aria-label Select2 already wrote is kept when present — it is the same
// text). A chip whose own `title` merely repeats its visible text loses it too:
// the browser raises the nearest titled ANCESTOR while the pointer rests on the
// remove button, which would stack the native tooltip over the bubble. A chip
// title that reveals more than the visible text (a templated selection) is a
// legitimate text affordance and stays. Returns how many button titles moved.
export function repairSelectionTitles(selection) {
  if (!selection?.querySelectorAll) return 0

  let moved = 0
  selection.querySelectorAll(TITLED_BUTTONS).forEach((button) => {
    const label = (button.getAttribute("title") || "").trim()
    button.removeAttribute("title")
    if (!label) return
    button.setAttribute(TOOLTIP_ATTR, label)
    if (!button.getAttribute("aria-label")) button.setAttribute("aria-label", label)
    moved += 1
  })

  selection.querySelectorAll(CHOICE_SELECTOR).forEach((choice) => {
    const display = choice.querySelector(CHOICE_DISPLAY_SELECTOR)
    const visible = (display?.textContent || "").trim()
    if (visible && choice.getAttribute("title").trim() === visible) choice.removeAttribute("title")
  })

  return moved
}

// Hand the multi-select span's combobox role and popup state to its search
// field. The span keeps the tabindex="-1" Select2 left it with, so it is
// neither focusable nor a control; the field already carries
// `aria-autocomplete="list"` and, while open, `aria-controls`. Returns false
// (and changes nothing) when there is no search field to receive the role.
export function transferComboboxRole(selection, search) {
  if (!selection || !search) return false

  const expanded = selection.getAttribute("aria-expanded") || "false"
  selection.removeAttribute("role")
  selection.removeAttribute("aria-haspopup")
  selection.removeAttribute("aria-expanded")

  search.setAttribute("role", "combobox")
  search.setAttribute("aria-haspopup", "listbox")
  search.setAttribute("aria-expanded", expanded)
  return true
}

// Select2 keeps writing `aria-expanded` (and `aria-owns`) on the span on every
// open/close; mirror the state onto the field and keep the span plain.
function syncExpandedState(selection, search, expanded) {
  selection.removeAttribute("aria-expanded")
  selection.removeAttribute("aria-owns")
  search.setAttribute("aria-expanded", expanded ? "true" : "false")
}

// Apply the repairs to one initialised widget and keep them applied across its
// re-renders. Listeners live on the Select2 instance and die with it on
// `select2("destroy")`, so nothing needs unwinding here. Returns false when the
// element has no rendered widget (the native-on-touch path, or a stand-in
// jQuery without a container).
export function installAccessibilityRepairs(element, $element) {
  const selection = renderedSelection(element, $element)
  if (!selection) return false

  const instance = $element?.data?.("select2")
  const search = multipleSearchField(selection)

  repairSelectionTitles(selection)
  transferComboboxRole(selection, search)

  if (typeof instance?.on === "function") {
    instance.on("selection:update", () => repairSelectionTitles(selection))
    if (search) {
      instance.on("open", () => syncExpandedState(selection, search, true))
      instance.on("close", () => syncExpandedState(selection, search, false))
    }
  }
  return true
}

// ── Dropdown placement correction (#13318) ──────────────────────────────────
//
// Select2's AttachBody adapter positions the open menu from jQuery `.offset()`
// arithmetic: the control's document offset minus the dropdownParent's. That
// maps document coordinates straight onto the parent's coordinate space, which
// is only right when the parent neither scrolls nor is zoomed. Two things in
// this app break it:
//
//   1. Modal hosts scroll. FormModal and gl_modal render a Bootstrap `.modal`
//      (position: fixed; overflow-y: auto) and consumers pass it as
//      `dropdownParent`. An absolutely positioned child scrolls WITH the modal's
//      content, so its `top` must include the modal's scrollTop. The library
//      never adds it, so once the admin has scrolled the dialog the menu lands
//      exactly scrollTop pixels above the field it belongs to.
//   2. `.gl-body` is zoomed to 0.9 on desktop (_layout.scss). The rects
//      `.offset()` reads are real viewport pixels, but the CSS `top`/`left` the
//      library writes are scaled by that zoom, so every menu — body-hosted
//      too — is painted at 0.9× its intended offset.
//
// Rather than re-derive containing blocks (static vs positioned parent, the
// initial containing block, nested scroll containers), measure the result:
// after the library positions the menu, compare where it actually landed with
// the edge of its control and shift its inline top/left by that delta in the
// menu's own (zoomed) units. Zoom is linear, so one pass is exact; the flip
// decision (`select2-dropdown--above`) stays the library's, and a menu that
// flips above is anchored by its bottom edge. Installed once per page on the
// adapter prototype, the first time the manager sees Select2, so every menu
// Select2 opens after that (any consumer, any `dropdownParent`) is corrected.

const ALIGNMENT_TOLERANCE_PX = 0.5
const ALIGNMENT_INSTALLED_FLAG = "__glDropdownAlignmentInstalled"
const ATTACH_BODY_MODULE = "select2/dropdown/attachBody"

// The factor by which CSS lengths inside `element` are scaled when painted.
// Chrome ≥ 128 / Firefox ≥ 126 expose it directly; elsewhere derive it from the
// rendered width over the layout width (offsetWidth is in layout units).
export function effectiveZoom(element) {
  if (!element) return 1
  const declared = element.currentCSSZoom
  if (typeof declared === "number" && declared > 0) return declared
  const layoutWidth = element.offsetWidth
  if (!layoutWidth) return 1
  const renderedWidth = element.getBoundingClientRect().width
  return renderedWidth > 0 ? renderedWidth / layoutWidth : 1
}

// Move `host` (the absolutely positioned `.select2-container--open` wrapper the
// menu lives in) so the painted menu's top edge meets the control's bottom edge
// — or, when the menu has flipped above, so its bottom edge meets the control's
// top. The wrapper itself is a zero-size anchor (its only child, the
// `.select2-dropdown`, is absolutely positioned at its top-left), so the box
// that is measured is the dropdown's; only the inline offset lives on the host.
// Returns the viewport-pixel delta that was corrected, or null when nothing
// measurable was passed.
export function alignDropdownToControl({ control, host, dropdown }) {
  if (!control || !host) return null

  const controlRect = control.getBoundingClientRect()
  const menu = dropdown || host
  const menuRect = menu.getBoundingClientRect()
  const above = Boolean(dropdown?.classList?.contains("select2-dropdown--above"))
  const targetTop = above ? controlRect.top - menuRect.height : controlRect.bottom

  const deltaTop = targetTop - menuRect.top
  const deltaLeft = controlRect.left - menuRect.left
  const aligned =
    Math.abs(deltaTop) < ALIGNMENT_TOLERANCE_PX && Math.abs(deltaLeft) < ALIGNMENT_TOLERANCE_PX
  if (aligned) return { top: 0, left: 0, zoom: 1, above }

  const zoom = effectiveZoom(menu)
  const currentTop = parseFloat(host.style.top) || 0
  const currentLeft = parseFloat(host.style.left) || 0
  host.style.top = `${currentTop + deltaTop / zoom}px`
  host.style.left = `${currentLeft + deltaLeft / zoom}px`

  return { top: deltaTop, left: deltaLeft, zoom, above }
}

// Wrap AttachBody.prototype._positionDropdown once so every placement the
// library performs (open, results:all / results:append, window scroll and
// resize) is followed by the correction above. Idempotent; a no-op when the
// Select2 build on the page does not expose its AMD modules.
export function installDropdownAlignment(jQuery) {
  const amd = jQuery?.fn?.select2?.amd
  if (!amd || typeof amd.require !== "function") return false

  let AttachBody = null
  try {
    AttachBody = amd.require(ATTACH_BODY_MODULE)
  } catch (_error) {
    return false
  }

  const proto = AttachBody?.prototype
  if (!proto || typeof proto._positionDropdown !== "function") return false
  if (proto[ALIGNMENT_INSTALLED_FLAG]) return true

  const original = proto._positionDropdown
  proto._positionDropdown = function alignedPositionDropdown(...args) {
    const result = original.apply(this, args)
    alignDropdownToControl({
      control: this.$container?.[0],
      host: this.$dropdownContainer?.[0],
      dropdown: this.$dropdown?.[0]
    })
    return result
  }
  proto[ALIGNMENT_INSTALLED_FLAG] = true
  return true
}

export function createSelect2Manager(options = {}) {
  const retryDelay = options.retryDelay ?? DEFAULT_RETRY_DELAY
  const retryLimit = options.retryLimit ?? DEFAULT_RETRY_LIMIT
  // Opt-in: on touch-primary devices skip Select2 entirely and let the browser
  // render the native <select>, which always opens on tap (see isTouchPrimary).
  const nativeOnTouch = options.nativeOnTouch ?? false

  const instances = new Map()
  const retryTimeouts = new Map()

  function init(element, optionsBuilder = {}, callbacks = {}) {
    if (!element) return
    attemptInit(element, optionsBuilder, callbacks, 0)
  }

  function attemptInit(element, optionsBuilder, callbacks, attemptCount) {
    if (nativeOnTouch && isTouchPrimary()) {
      prepareNativeFallback(element, callbacks)
      return
    }

    const jQuery = select2Available()

    if (!jQuery) {
      if (attemptCount >= retryLimit) {
        console.warn("Select2 failed to initialise after", attemptCount, "attempts", element)
        return
      }

      clearRetry(element)
      const timeout = window.setTimeout(
        () => attemptInit(element, optionsBuilder, callbacks, attemptCount + 1),
        retryDelay
      )
      retryTimeouts.set(element, timeout)
      return
    }

    clearRetry(element)
    installDropdownAlignment(jQuery)

    const $element = jQuery(element)
    if ($element.data("select2")) {
      $element.select2("destroy")
    }

    const providedOptions =
      typeof optionsBuilder === "function"
        ? optionsBuilder(element, $element)
        : optionsBuilder || {}

    $element.select2(providedOptions)
    installAccessibilityRepairs(element, $element)
    mirrorAccessibleName(element, $element)

    let cleanup = null
    if (typeof callbacks.onInitialized === "function") {
      cleanup = callbacks.onInitialized(element, $element) || null
    }

    instances.set(element, { $element, cleanup })
  }

  function clearRetry(element) {
    const timeout = retryTimeouts.get(element)
    if (timeout) {
      window.clearTimeout(timeout)
      retryTimeouts.delete(element)
    }
  }

  // Make the native control a usable stand-in for the skipped Select2 widget.
  // A <select multiple> with no size collapses to a couple of rows; give it a
  // sensible visible row count so the inline list is comfortable to use. Single
  // selects need nothing — the browser's native picker is already ideal.
  //
  // Still run callbacks.onInitialized so behavior wired there survives the
  // native path: handlers attached via jQuery `.on("change")` fire on the
  // native control too, so e.g. a selection counter keeps working. The instance
  // is tracked (with a null $element) so destroy()/destroyAll() runs its cleanup.
  //
  // Callers wire handlers via the jQuery-wrapped element ($element.on(...)),
  // which is always available here (jQuery ships as Select2's dependency). If it
  // somehow isn't, skip onInitialized rather than pass a raw DOM node that would
  // throw on `.on` — the native control still works, just without the extra
  // wiring.
  function prepareNativeFallback(element, callbacks = {}) {
    let detachSummary = null

    // Collapse on ANY touch-primary device, not just phone widths. This function
    // is only reached when isTouchPrimary() is already true, and the sheet takes
    // the tap for anything carrying the marker at any width — so a tablet is
    // handled the same as a phone (#9241). Width decides nothing here, which is
    // also why rotation cannot strand a collapsed select with a dead trigger.
    if (element.multiple) {
      element.removeAttribute("size")
      detachSummary = attachMobileSelectSummary(element, {
        placeholder: element.dataset.select2Placeholder || undefined,
      })
    }

    let wiredCleanup = null
    const jQuery = window.jQuery || window.$
    if (typeof callbacks.onInitialized === "function" && jQuery) {
      wiredCleanup = callbacks.onInitialized(element, jQuery(element)) || null
    }

    const cleanup = wiredCleanup || detachSummary
      ? () => {
        wiredCleanup?.()
        detachSummary?.()
      }
      : null

    instances.set(element, { $element: null, cleanup })
  }

  function destroy(element) {
    if (!element) return

    clearRetry(element)

    const record = instances.get(element)
    if (!record) return

    const { $element, cleanup } = record

    if (typeof cleanup === "function") {
      try {
        cleanup()
      } catch (error) {
        console.warn("Error during select2 cleanup", error)
      }
    }

    if ($element && $element.data("select2")) {
      $element.select2("destroy")
    }

    instances.delete(element)
  }

  function destroyAll() {
    Array.from(instances.keys()).forEach((element) => destroy(element))
    retryTimeouts.forEach((timeout) => window.clearTimeout(timeout))
    retryTimeouts.clear()
    instances.clear()
  }

  return {
    init,
    destroy,
    destroyAll
  }
}
